import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/modern/components/ui/button";
import { Textarea } from "@/modern/components/ui/textarea";
import {
  FileUpload,
  FileUploadDropzone,
  FileUploadItem,
  FileUploadItemDelete,
  FileUploadItemPreview,
} from "@/modern/components/ui/file-upload";
import { BaseModal } from "../../base/BaseModal.jsx";
import { BaseModalBody } from "../../base/BaseModalBody.jsx";
import { Upload, Sparkles } from "lucide-react";
import { uploadFile, generateAIComments } from "@/network/network.jsx";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";
import { toast } from "sonner";

const MAX_SIZE = 5 * 1024 * 1024; // 5MB

interface AddDetailsPopoverProps {
  onAddDetail: (detail: { image?: File; comment: string; document_id?: number; id?: string }) => void;
  onEditDetail?: (id: string, detail: { image?: File; comment: string; document_id?: number }) => void;
  editingDetail?: { id: string; image?: File; imageUrl?: string; comment: string; document_id?: number };
  onCancelEdit?: () => void;
  children: React.ReactNode;
  editorInstance?: unknown;
}

const SpotIncentiveDetailModal = ({ onAddDetail, onEditDetail, editingDetail, onCancelEdit, children, editorInstance }: AddDetailsPopoverProps) => { // eslint-disable-line @typescript-eslint/no-explicit-any
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState<File[]>(editingDetail?.image ? [editingDetail.image] : []);
  const [imageUrl, setImageUrl] = useState<string | undefined>(editingDetail?.imageUrl);
  const [comment, setComment] = useState(editingDetail?.comment || "");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [documentId, setDocumentId] = useState<number | undefined>(editingDetail?.document_id);
  const uploadInProgressRef = useRef(false);

  // Update state when editingDetail changes
  useEffect(() => {
    if (editingDetail) {
      setFiles(editingDetail.image ? [editingDetail.image] : []);
      setImageUrl(editingDetail.imageUrl);
      setComment(editingDetail.comment || "");
      setDocumentId(editingDetail.document_id);
      setOpen(true);
    }
  }, [editingDetail]);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setFiles([]);
      setImageUrl(undefined);
      setComment("");
      setDocumentId(undefined);
      uploadInProgressRef.current = false;
      if (editingDetail && onCancelEdit) {
        onCancelEdit();
      }
    }
  };

  const onFileValidate = (file: File) => {
    if (!file.type.startsWith("image/")) return "Only image files are allowed";
    if (file.size > MAX_SIZE)
      return `File size must be less than ${MAX_SIZE / (1024 * 1024)}MB`;
    return null;
  };

  const onValueChange = async (newFiles: File[]) => {
    setFiles(newFiles);

    // Clear imageUrl when a new file is selected
    if (newFiles.length > 0) {
      setImageUrl(undefined);
    }

    // Only upload if:
    // 1. We have files (not removing all files)
    // 2. This is a new file (different from current)
    // 3. We're not already uploading
    // 4. This is actually a new file selection (not just clearing)
    if (newFiles.length > 0 &&
        (files.length === 0 || (newFiles[0] && newFiles[0] !== files[0])) &&
        !uploadInProgressRef.current) {

      uploadInProgressRef.current = true;
      setIsUploading(true);

      try {
        const uploadedFile = newFiles[0];
        const response = await uploadFile(uploadedFile, uploadedFile.name);
        const { id } = response.data;

        // Store the file id as document_id for backend compatibility
        setDocumentId(id);
      } catch (error) {
        toast.error("Upload failed", {
          description: "Unable to upload image. Please try again."
        });
        // Remove the file if upload failed
        setFiles([]);
        setDocumentId(undefined);
      } finally {
        uploadInProgressRef.current = false;
        setIsUploading(false);
      }
    }
  };

  const handleAIAnalysis = async () => {
    if (files.length === 0 && !imageUrl && !comment.trim()) return;

    setIsAnalyzing(true);

    try {
      // Get board ID from context or default
      const selectedBoard = getSavedBoardSelection() || { id: 1 }; // eslint-disable-line @typescript-eslint/no-explicit-any

      // Call the AI comment generation API
      const result = await generateAIComments({
        document_id: documentId || 0,
        comment: comment.trim(),
        board_id: selectedBoard.id
      });

      // Set the AI-generated comment
      setComment((result.data.comment || "").trim());

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      let errorMessage = "Unable to generate analysis. Please try again.";
      // Check for the specific error structure from AI endpoints
      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail[0] || errorMessage;
        } else {
          errorMessage = error.response.data.detail;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      toast.error("AI Analysis failed", {
        description: errorMessage
      });
      // Show error to user - don't modify comment on failure
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSave = () => {
    if (files.length === 0 && !imageUrl && !comment.trim()) {
      return;
    }

    const currentImage = files.length > 0 ? files[0] : undefined;

    // Only save if documentId exists (file was uploaded successfully) or no file was selected or we have an imageUrl
    if (files.length === 0 || imageUrl || documentId !== undefined) {
      if (editingDetail && onEditDetail) {
        onEditDetail(editingDetail.id, {
          image: currentImage,
          comment: comment.trim(),
          document_id: documentId
        });
      } else {
        onAddDetail({
          image: currentImage,
          comment: comment.trim(),
          document_id: documentId,
          id: documentId?.toString()
        });
      }

      // Reset form
      setFiles([]);
      setImageUrl(undefined);
      setComment("");
      setDocumentId(undefined);
      uploadInProgressRef.current = false;
      handleOpenChange(false);
    }
  };

  const MODAL_SIZE_CONFIG = {
    width: "min(90vw, 44rem)",
    height: "min(90vh, 42rem)",
    minWidth: "min(90vw, 44rem)",
    minHeight: "min(90vw, 42rem)",
    maxWidth: "min(90vw, 80rem)",
    maxHeight: "min(90vh, 80rem)"
  };

  return (
    <>
      <div onClick={() => setOpen(true)}>
        {children}
      </div>
      {open && (
        <BaseModal
          title={editingDetail ? "Edit Detail" : "Add Detail"}
          description={"Upload an image and add comments or analysis"}
          className="resize-none"
          onClose={() => handleOpenChange(false)}
          size={MODAL_SIZE_CONFIG}
          editorInstance={editorInstance}
          record={{}}
          headerIconCls=""
          modal={false}
          version=""
          swivuAssistanceConfig={null}
          hideMenu={true}
          footerRenderer={() => (
            <div className="flex gap-2 pt-2 justify-end">
              <Button
                variant="outline"
                onClick={() => handleOpenChange(false)}
                className="text-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={isUploading || isAnalyzing || (files.length === 0 && !imageUrl && !comment.trim())}
                className="text-sm"
              >
                {isUploading ? "Uploading..." : isAnalyzing ? "Analyzing..." : "Done"}
              </Button>
            </div>
          )}
        >
          <BaseModalBody>
            <div className="space-y-4">

          {/* Image Upload */}
          <div className="space-y-2">
            <FileUpload
              value={files}
              onValueChange={onValueChange}
              onFileValidate={onFileValidate}
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/bmp"
              className="w-full gap-swivu-base"
              maxFiles={1}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-foreground">
                    Upload Photo (Optional)
                  </h3>
                </div>
              </div>
              <FileUploadDropzone isClickEnabled={files.length === 0 && !imageUrl} className={(files.length > 0 || imageUrl) ? "h-52" : "h-32"}>
                {!files.length && !imageUrl && <div className="space-y-swivu-vertical text-center">
                  <Upload className="h-8 w-8 text-swivu-icon mx-auto" />
                  <p className="font-medium text-sm leading-none">
                    Drag & drop image here
                  </p>
                  <p className="text-muted-foreground text-sm leading-none">
                    Or click to browse
                  </p>
                </div>}
                {/* Show image from URL if no files are selected */}
                {!files.length && imageUrl && (
                  <div className="group relative block w-full h-full !p-0">
                    <img
                      src={imageUrl}
                      alt="Preview"
                      className="w-full h-full object-contain rounded-md hover:opacity-90 transition-opacity"
                    />
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      onClick={() => setImageUrl(undefined)}
                    >
                      <i className="fa fa-xmark"></i>
                    </Button>
                  </div>
                )}
                {files.map((file, index) => (
                  <FileUploadItem key={index} value={file} className="group relative block w-full h-full !p-0">
                    <div className="cursor-pointer size-full">
                      <FileUploadItemPreview className="w-full h-full hover:opacity-90 transition-opacity" />
                    </div>
                    <FileUploadItemDelete
                      asChild
                      className="absolute top-2 right-2 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    >
                      <Button variant="destructive" size="icon">
                        <i className="fa fa-xmark"></i>
                      </Button>
                    </FileUploadItemDelete>
                  </FileUploadItem>
                ))}
              </FileUploadDropzone>
            </FileUpload>
          </div>

          {/* Comment */}
          <div className="space-y-2">
            <label className="block text-sm font-bold text-foreground">Comment</label>
            <Textarea
              placeholder="Add important details, observations, or notes..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="min-h-32 resize-none text-sm placeholder:text-sm"
            />
          </div>

          {/* AI Analysis Button */}
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={handleAIAnalysis}
              disabled={isAnalyzing || isUploading || (files.length === 0 && !imageUrl && !comment.trim())}
            >
              <Sparkles className="h-3 w-3 mr-2" />
              {isAnalyzing ? "Analyzing..." : "AI Analysis"}
            </Button>
          </div>
        </div>
      </BaseModalBody>
    </BaseModal>
      )}
    </>
  );
};

export default SpotIncentiveDetailModal;
